<template>
  <div class="p-6 md:p-10 bg-white min-h-screen font-sans text-slate-800 select-none">
    
    <!-- HEADER / TITRE -->
    <header class="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 border-b border-slate-100 pb-6">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <span class="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black uppercase rounded-full tracking-wider border border-blue-100">
            {{ t('helpCenter') }}
          </span>
        </div>
        <h1 class="text-3xl font-black text-slate-900 tracking-tight">{{ t('dashboardTitle') }}</h1>
        <p class="text-xs text-slate-500 font-medium mt-1">{{ t('dashboardSubtitle') }}</p>
      </div>

      <button @click="showNewTicketModal = true" class="px-5 py-3 bg-blue-600 text-white rounded-xl font-bold text-xs transition-all hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-500/20 flex items-center gap-2 cursor-pointer">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/></svg>
        {{ t('newRequest') }}
      </button>
    </header>

    <main class="max-w-7xl mx-auto space-y-4">
      
      <!-- SECTION FILTRES & RECHERCHE AVANCÉE -->
      <div class="bg-slate-50/80 p-4 rounded-2xl border border-slate-200/80 flex flex-col md:flex-row gap-3 justify-between items-center shadow-sm">
        
        <!-- Barre de recherche textuelle -->
        <div class="relative w-full md:w-80">
          <svg class="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input 
            v-model="searchQuery" 
            type="text" 
            :placeholder="t('searchPlaceholder')" 
            class="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs font-semibold text-slate-800 outline-none focus:border-blue-600 transition-all shadow-sm"
          />
        </div>

        <!-- Filtres par Statut -->
        <div class="flex gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          <button v-for="s in ['ALL', 'OPEN', 'IN_PROGRESS', 'RESOLVED']" :key="s"
            @click="filterStatus = s"
            :class="filterStatus === s ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'"
            class="px-3.5 py-2 text-[11px] font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            {{ t(s.toLowerCase()) }}
          </button>
        </div>

        <!-- Filtre par Priorité -->
        <div class="w-full md:w-48">
          <select 
            v-model="filterPriority"
            class="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-semibold text-slate-800 outline-none focus:border-blue-600 transition-all cursor-pointer shadow-sm"
          >
            <option value="">{{ t('allPriorities') }}</option>
            <option value="LOW">Basse</option>
            <option value="MEDIUM">Moyenne</option>
            <option value="URGENT">Critique</option>
          </select>
        </div>

      </div>

      <!-- TABLEAU DES TICKETS (DATA GRID OPTIMISÉ +1000 TICKETS) -->
      <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-slate-50 border-b border-slate-200/80 text-[10px] font-black uppercase tracking-widest text-slate-400">
                <th class="py-3.5 px-4"># ID</th>
                <th class="py-3.5 px-4">Priorité</th>
                <th class="py-3.5 px-6">Sujet / Description</th>
                <th class="py-3.5 px-4">Statut</th>
                <th class="py-3.5 px-4">Dernière mise à jour</th>
                <th class="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 text-xs">
              <tr 
                v-for="ticket in paginatedTickets" 
                :key="ticket.id || ticket._id"
                @click="openTicketDetails(ticket)"
                class="hover:bg-blue-50/40 transition-all cursor-pointer group"
              >
                <!-- ID -->
                <td class="py-3.5 px-4 font-mono font-bold text-slate-400">
                  #{{ (ticket.id || ticket._id).slice(-4) }}
                </td>

                <!-- Priorité -->
                <td class="py-3.5 px-4">
                  <span :class="getPriorityTheme(ticket.priority)" class="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border">
                    {{ ticket.priority || 'Moyenne' }}
                  </span>
                </td>

                <!-- Sujet et aperçu de la description -->
                <td class="py-3.5 px-6 max-w-xs md:max-w-md">
                  <p class="font-bold text-slate-900 group-hover:text-blue-600 transition-colors truncate">{{ ticket.subject }}</p>
                  <p class="text-slate-400 text-[11px] truncate mt-0.5 font-medium">{{ ticket.description || ticket.message }}</p>
                </td>

                <!-- Statut -->
                <td class="py-3.5 px-4">
                  <span :class="getStatusTheme(ticket.status)" class="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border">
                    {{ t((ticket.status || 'OPEN').toLowerCase()) }}
                  </span>
                </td>

                <!-- Date -->
                <td class="py-3.5 px-4 text-slate-500 font-medium whitespace-nowrap">
                  {{ formatTime(ticket.updatedAt || ticket.date) }}
                </td>

                <!-- Action bouton visuel -->
                <td class="py-3.5 px-4 text-right">
                  <button class="px-3 py-1.5 bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-600 rounded-lg font-bold text-[11px] transition-all">
                    {{ t('details') }}
                  </button>
                </td>
              </tr>

              <!-- État vide -->
              <tr v-if="paginatedTickets.length === 0 && !isLoading">
                <td colspan="6" class="text-center py-16 text-slate-400 font-bold uppercase tracking-wider text-xs">
                  {{ t('noTickets') || "Aucun ticket ne correspond à vos critères" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- PAGINATION POUR +1000 TICKETS -->
        <div class="p-4 bg-slate-50 border-t border-slate-200/80 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p class="text-slate-500 font-medium">
            Affichage de <span class="font-bold text-slate-800">{{ startIndex + 1 }}</span> à <span class="font-bold text-slate-800">{{ endIndex }}</span> sur <span class="font-bold text-slate-800">{{ filteredTickets.length }}</span> tickets
          </p>

          <div class="flex items-center gap-2">
            <button 
              @click="currentPage--" 
              :disabled="currentPage === 1"
              class="px-3 py-1.5 bg-white border border-slate-200 rounded-lg font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              {{ t('prev') }}
            </button>
            <span class="px-3 py-1 text-slate-700 font-bold">
              Page {{ currentPage }} / {{ totalPages || 1 }}
            </span>
            <button 
              @click="currentPage++" 
              :disabled="currentPage >= totalPages"
              class="px-3 py-1.5 bg-white border border-slate-200 rounded-lg font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              {{ t('next') }}
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- MODAL DETAILS TICKET -->
    <Transition name="fade">
      <div v-if="selectedTicket" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="selectedTicket = null"></div>
        <div class="relative bg-white w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-xl flex flex-col overflow-hidden border border-slate-200">
          <div class="p-6 bg-slate-50 border-b border-slate-200/80 flex justify-between items-center">
            <div class="flex items-center gap-3">
              <span class="w-3 h-3 rounded-full" :class="getPriorityTheme(selectedTicket.priority)"></span>
              <h2 class="text-base font-black text-slate-900">{{ selectedTicket.subject }}</h2>
            </div>
            <button @click="selectedTicket = null" class="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-all text-lg font-bold cursor-pointer">&times;</button>
          </div>
          <div class="flex-1 overflow-y-auto p-6 space-y-6">
            <section>
              <div class="flex items-center gap-3 mb-3">
                <span class="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-black uppercase tracking-wider">{{ t('myRequest') }}</span>
                <div class="h-[1px] flex-1 bg-slate-200/80"></div>
                <span class="text-[11px] text-slate-400 font-medium">{{ formatTime(selectedTicket.createdAt || selectedTicket.date) }}</span>
              </div>
              <div class="bg-slate-50 rounded-xl p-4 border border-slate-200/80">
                <p class="text-slate-700 text-xs leading-relaxed font-medium">{{ selectedTicket.description || selectedTicket.message }}</p>
              </div>
            </section>
            
            <section v-if="selectedTicket.status !== 'OPEN' && selectedTicket.status !== 'Ouvert'">
              <div class="flex items-center gap-3 mb-3">
                <span class="px-2.5 py-1 bg-blue-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider">{{ t('supportReply') }}</span>
                <div class="h-[1px] flex-1 bg-blue-100"></div>
                <span class="text-[11px] text-blue-500 font-medium italic">{{ t('justNow') }}</span>
              </div>
              <div class="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                <p class="text-blue-900 text-xs leading-relaxed font-medium" v-html="t('supportMessage')"></p>
              </div>
            </section>
            
            <div v-else class="py-10 text-center">
              <div class="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-3 border border-blue-100">
                <div class="w-2 h-2 bg-blue-600 rounded-full animate-ping"></div>
              </div>
              <p class="text-xs font-bold text-slate-400">{{ t('analyzingRequest') }}</p>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- MODAL CREATION DE TICKET -->
    <Transition name="fade">
      <div v-if="showNewTicketModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="showNewTicketModal = false"></div>
        <div class="relative bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden border border-slate-200">
          <div class="p-6 bg-slate-50 border-b border-slate-200/80 flex justify-between items-center">
            <div>
              <h2 class="text-lg font-black text-slate-900">{{ t('createTicketTitle') }}</h2>
              <p class="text-[11px] text-slate-500 font-medium mt-0.5">{{ t('createTicketSubtitle') }}</p>
            </div>
            <button @click="showNewTicketModal = false" class="w-8 h-8 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 transition-all text-lg font-bold flex items-center justify-center cursor-pointer">&times;</button>
          </div>

          <form @submit.prevent="submitTicket" class="p-6 space-y-4">
            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">{{ t('subjectLabel') }}</label>
              <input v-model="newTicket.subject" required type="text" :placeholder="t('subjectPlaceholder')" 
                     class="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-semibold text-slate-800 outline-none focus:border-blue-600 focus:bg-white transition-all shadow-sm" />
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">{{ t('priorityLabel') }}</label>
              <div class="grid grid-cols-3 gap-2">
                <button v-for="p in ['LOW', 'MEDIUM', 'URGENT']" :key="p" type="button"
                        @click="newTicket.priority = p"
                        :class="newTicket.priority === p ? 'border-blue-600 bg-blue-50 text-blue-700 font-bold' : 'border-slate-200 bg-slate-50 text-slate-600'"
                        class="py-2.5 px-2 border rounded-xl text-[10px] font-black transition-all uppercase cursor-pointer">
                  {{ t(p.toLowerCase()) }}
                </button>
              </div>
            </div>

            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">{{ t('descriptionLabel') }}</label>
              <textarea v-model="newTicket.description" required rows="3" :placeholder="t('descriptionPlaceholder')"
                        class="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-semibold text-slate-800 outline-none focus:border-blue-600 focus:bg-white transition-all resize-none shadow-sm"></textarea>
            </div>

            <button type="submit" 
                    class="w-full py-3.5 bg-blue-600 text-white rounded-xl font-bold uppercase tracking-wider text-xs shadow-md shadow-blue-500/20 hover:bg-blue-700 transition-all cursor-pointer mt-2">
              {{ t('sendRequest') }}
            </button>
          </form>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';

const lang = ref(localStorage.getItem('appLanguage') || 'fr');
const filterStatus = ref('ALL');
const filterPriority = ref('');
const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10); // Pagination optimisée pour charger proprement l'affichage

const selectedTicket = ref(null);
const showNewTicketModal = ref(false);
const isLoading = ref(false);

const newTicket = ref({
  subject: '',
  priority: 'MEDIUM',
  description: ''
});

const translations = {
  fr: {
    helpCenter: "Centre d'aide & Support",
    dashboardTitle: "Gestion des Tickets",
    dashboardSubtitle: "Gérez vos incidents et suivez l'évolution de vos demandes techniques.",
    newRequest: "Nouveau ticket",
    all: "Tous",
    open: "Ouvert",
    in_progress: "En cours",
    resolved: "Résolu",
    low: "Basse",
    medium: "Moyenne",
    urgent: "Critique",
    allPriorities: "Toutes les priorités",
    searchPlaceholder: "Rechercher par sujet ou ID...",
    details: "Voir",
    prev: "Précédent",
    next: "Suivant",
    lastUpdate: "Dernière mise à jour",
    myRequest: "Ma Demande",
    supportReply: "Réponse Support",
    justNow: "À l'instant",
    analyzingRequest: "Votre demande est en cours d'analyse par le support...",
    supportMessage: "Bonjour,<br><br>Votre demande a bien été prise en compte par nos équipes techniques.<br><br>Cordialement,<br><strong>Le Support Medialink</strong>",
    createTicketTitle: "Ouvrir un ticket",
    createTicketSubtitle: "Décrivez votre problème en détail",
    subjectLabel: "Sujet de la demande",
    subjectPlaceholder: "Ex: Problème d'affichage borne",
    priorityLabel: "Niveau de priorité",
    descriptionLabel: "Description détaillée",
    descriptionPlaceholder: "Expliquez précisément votre situation...",
    sendRequest: "Envoyer la demande"
  },
  en: {
    helpCenter: "Help Center & Support",
    dashboardTitle: "Ticket Management",
    dashboardSubtitle: "Manage your incidents and track the status of your technical requests.",
    newRequest: "New Ticket",
    all: "All",
    open: "Open",
    in_progress: "In Progress",
    resolved: "Resolved",
    low: "Low",
    medium: "Medium",
    urgent: "Critical",
    allPriorities: "All priorities",
    searchPlaceholder: "Search by subject or ID...",
    details: "View",
    prev: "Previous",
    next: "Next",
    lastUpdate: "Last update",
    myRequest: "My Request",
    supportReply: "Support Reply",
    justNow: "Just now",
    analyzingRequest: "Your request is being analyzed by support...",
    supportMessage: "Hello,<br><br>Your request has been processed by our technical team.<br><br>Best regards,<br><strong>Medialink Support</strong>",
    createTicketTitle: "Open a ticket",
    createTicketSubtitle: "Describe your issue in detail",
    subjectLabel: "Request Subject",
    subjectPlaceholder: "Ex: Kiosk display issue",
    priorityLabel: "Priority Level",
    descriptionLabel: "Detailed Description",
    descriptionPlaceholder: "Explain your situation precisely...",
    sendRequest: "Submit Request"
  },
  de: {
    helpCenter: "Hilfecenter & Support",
    dashboardTitle: "Ticket-Verwaltung",
    dashboardSubtitle: "Verwalten Sie Ihre Vorfälle und verfolgen Sie Ihre technischen Anfragen.",
    newRequest: "Neues Ticket",
    all: "Alle",
    open: "Offen",
    in_progress: "In Bearbeitung",
    resolved: "Gelöst",
    low: "Niedrig",
    medium: "Mittel",
    urgent: "Kritisch",
    allPriorities: "Alle Prioritäten",
    searchPlaceholder: "Nach Betreff oder ID suchen...",
    details: "Ansehen",
    prev: "Zurück",
    next: "Weiter",
    lastUpdate: "Letzte Aktualisierung",
    myRequest: "Meine Anfrage",
    supportReply: "Support-Antwort",
    justNow: "Gerade eben",
    analyzingRequest: "Ihre Anfrage wird vom Support analysiert...",
    supportMessage: "Hallo,<br><br>Ihre Anfrage wurde von unserem technischen Team bearbeitet.<br><br>Mit freundlichen Grüßen,<br><strong>Medialink Support</strong>",
    createTicketTitle: "Ticket öffnen",
    createTicketSubtitle: "Beschreiben Sie Ihr Problem im Detail",
    subjectLabel: "Betreff",
    subjectPlaceholder: "Z.B.: Kiosk-Anzeigeproblem",
    priorityLabel: "Prioritätsstufe",
    descriptionLabel: "Detaillierte Beschreibung",
    descriptionPlaceholder: "Erklären Sie Ihre Situation genau...",
    sendRequest: "Anfrage senden"
  },
  it: {
    helpCenter: "Centro Assistenza",
    dashboardTitle: "Gestione Ticket",
    dashboardSubtitle: "Gestisci i tuoi incidenti e segui lo stato delle tue richieste tecniche.",
    newRequest: "Nuovo Ticket",
    all: "Tutti",
    open: "Aperto",
    in_progress: "In corso",
    resolved: "Risolto",
    low: "Bassa",
    medium: "Media",
    urgent: "Critico",
    allPriorities: "Tutte le priorità",
    searchPlaceholder: "Cerca per oggetto o ID...",
    details: "Vedi",
    prev: "Precedente",
    next: "Successivo",
    lastUpdate: "Ultimo aggiornamento",
    myRequest: "La mia richiesta",
    supportReply: "Risposta Supporto",
    justNow: "Proprio ora",
    analyzingRequest: "La tua richiesta è in fase di analisi...",
    supportMessage: "Ciao,<br><br>La tua richiesta è stata presa in carico dal nostro team tecnico.<br><br>Cordiali saluti,<br><strong>Supporto Medialink</strong>",
    createTicketTitle: "Apri un ticket",
    createTicketSubtitle: "Descrivi il tuo problema in dettaglio",
    subjectLabel: "Oggetto",
    subjectPlaceholder: "Es: Problema display totem",
    priorityLabel: "Livello di priorità",
    descriptionLabel: "Descrizione dettagliata",
    descriptionPlaceholder: "Spiega precisamente la tua situazione...",
    sendRequest: "Invia richiesta"
  }
};

const t = (key) => {
  return translations[lang.value]?.[key] || translations['fr'][key] || key;
};

const tickets = ref([]);

const fetchTickets = async () => {
  isLoading.value = true;
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('userToken');
    const response = await fetch('https://api.datafuse.fr/api/agency/myticket', {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Erreur réseau');
    const data = await response.json();
    tickets.value = data.tickets || data;
  } catch (err) {
    console.error("❌ Erreur chargement tickets:", err);
  } finally {
    isLoading.value = false;
  }
};

const submitTicket = async () => {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('userToken');
    const priorityMap = {
      'LOW': 'Basse',
      'MEDIUM': 'Moyenne',
      'URGENT': 'Critique'
    };

    const response = await fetch('https://api.datafuse.fr/api/agency/myticket', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        client: { name: "Dashboard Pharmacie" },
        subject: newTicket.value.subject,
        message: newTicket.value.description,
        priority: priorityMap[newTicket.value.priority] || 'Moyenne', 
        category: 'Technique',
        metadata: { source: 'web' }
      })
    });

    if (response.ok) {
      showNewTicketModal.value = false;
      newTicket.value = { subject: '', priority: 'MEDIUM', description: '' };
      await fetchTickets();
    } else {
      const errorData = await response.json();
      console.error("Erreur API:", errorData);
    }
  } catch (err) {
    console.error("❌ Erreur lors de l'envoi du ticket:", err);
  }
};

const openTicketDetails = async (ticket) => {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('userToken');
    const ticketId = ticket.id || ticket._id;
    const response = await fetch(`https://api.datafuse.fr/api/agency/myticket/${ticketId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    selectedTicket.value = await response.json();
  } catch (err) {
    selectedTicket.value = ticket;
  }
};

// Logique de filtrage combinée (Statut + Priorité + Recherche textuelle)
const filteredTickets = computed(() => {
  return tickets.value.filter(ticket => {
    const matchesStatus = filterStatus.value === 'ALL' || (ticket.status || '').toUpperCase() === filterStatus.value.toUpperCase();
    const matchesPriority = !filterPriority.value || (ticket.priority || '').toUpperCase().includes(filterPriority.value.toUpperCase());
    
    const query = searchQuery.value.toLowerCase();
    const matchesSearch = !query || 
      (ticket.subject && ticket.subject.toLowerCase().includes(query)) || 
      (ticket.description && ticket.description.toLowerCase().includes(query)) ||
      (ticket.message && ticket.message.toLowerCase().includes(query)) ||
      ((ticket.id || ticket._id) && (ticket.id || ticket._id).toLowerCase().includes(query));

    return matchesStatus && matchesPriority && matchesSearch;
  });
});

// Réinitialiser la pagination lors du changement des filtres
watch([filterStatus, filterPriority, searchQuery], () => {
  currentPage.value = 1;
});

// Propriétés calculées pour la pagination (+1000 tickets)
const totalPages = computed(() => Math.ceil(filteredTickets.value.length / itemsPerPage.value));
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() => Math.min(startIndex.value + itemsPerPage.value, filteredTickets.value.length));

const paginatedTickets = computed(() => {
  return filteredTickets.value.slice(startIndex.value, endIndex.value);
});

const getPriorityTheme = (p) => {
  const priority = String(p).toUpperCase();
  if (priority.includes('CRITIQUE') || priority.includes('URGENT') || priority.includes('HAUTE')) return 'bg-rose-50 text-rose-700 border-rose-200';
  if (priority.includes('MOYENNE') || priority.includes('MEDIUM')) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
};

const getStatusTheme = (s) => {
  const status = String(s).toUpperCase();
  if (status === 'OPEN' || status === 'OUVERT') return 'bg-slate-100 text-slate-700 border-slate-200';
  if (status === 'IN-PROGRESS' || status === 'IN_PROGRESS' || status === 'EN COURS') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (status === 'DONE' || status === 'RESOLVED' || status === 'RÉSOLU') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const formatTime = (dateStr) => {
  if (!dateStr) return '--:--';
  const date = new Date(dateStr);
  return date.toLocaleDateString([], { day: '2-digit', month: '2-digit' }) + ' à ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

onMounted(async () => {
  await fetchTickets();
});
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>